import * as React from "react";
import theme from "styles/theme";
import { ImageIcon } from "components/icons/assortments/image";
import { formatMessage } from "lib/globalize-util";
import { merge, $, media } from "glamor";
import { ITileSettings, ITileSettingsValue } from "./types";
import { DATATEST } from "constants/integration";

const styles = merge(
    {
        width: "380px !important",
        paddingRight: "80px",
    },
    $(" .imageContainer", {
        border: `2px solid ${theme.palette.borderColor}`,
    }),
    $(" .noImageIcon", {
        height: "200px !important",
        width: "120px !important",
        display: "block !important",
        margin: "0 auto",
        color: `${theme.palette.grey} !important`,
    }),
    $(" .detailsContainer", {
        margin: "4px 0 16px",
        color: theme.palette.tileItemText,
    }),
    $(" .detailsContainer .bold", {
        fontSize: "12px",
        fontWeight: "bold",
        color: theme.palette.textColor,
    }),
    $(" .detailsContainer p", {
        margin: 0,
        fontSize: "10px",
        letterSpacing: "0.15px",
        lineHeight: "16px",
    }),
    $(" .totalsContainer", {
        boxShadow: `0 1px 0 0 ${theme.palette.tileItemShadowColor}`,
        paddingBottom: "8px",
    }),
    $(" .totalsContainer p", {
        margin: 0,
        color: theme.palette.darkGrey,
        fontSize: "10px",
        letterSpacing: "0.15px",
        lineHeight: "16px",
        fontWeight: "bold",
    }),
    $(" .colorContainer", {
        display: "flex",
        alignItems: "center",
    }),
    $(" .colorContainer span", {
        lineHeight: "22px",
        fontSize: "11px",
    }),
    $(" .colorSwatch", {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        border: `solid 1px ${theme.palette.swatchBorderColor}`,
        marginRight: "4px",
        backgroundColor: theme.palette.swatchBackgroundColor,
    }),
    media("(max-width: 768px)", {
        width: "250px !important",
        margin: "0 auto 20px",
        paddingRight: 0,
    }),
    media("(max-width: 600px)", {
        width: "220px !important",
    }),
    media("(max-width: 440px)", {
        width: "200px !important",
    })
);
interface IProps {
    tileSettings: ITileSettings;
    tileSettingsValue: ITileSettingsValue;
    hasAllocationDimensions: boolean;
}

export const PreviewPanelView: React.FunctionComponent<IProps> = ({
    tileSettings,
    tileSettingsValue,
    hasAllocationDimensions,
}) => (
    <div {...styles}>
        <div className="imageContainer">
            <ImageIcon className="noImageIcon" />
        </div>
        <div className="detailsContainer">
            {tileSettings.firstLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.FIRST_LINE}>
                    {tileSettingsValue[tileSettings.firstLine]}
                </p>
            )}
            {tileSettings.secondLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.SECOND_LINE} className="bold">
                    {tileSettingsValue[tileSettings.secondLine]}
                </p>
            )}
            {tileSettings.thirdLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.THIRD_LINE}>
                    {tileSettingsValue[tileSettings.thirdLine]}
                </p>
            )}
            <div className="colorContainer">
                {tileSettings.showColorSwatch && (
                    <span
                        data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.COLOR_SWATCH}
                        className="colorSwatch"
                    />
                )}
                {tileSettings.fourthLine && (
                    <span data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.FOURTH_LINE}>
                        {tileSettingsValue[tileSettings.fourthLine]}
                    </span>
                )}
            </div>
            {tileSettings.fifthLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.FIFTH_LINE}>
                    {tileSettingsValue[tileSettings.fifthLine]}
                </p>
            )}
            {tileSettings.sixthLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.SIXTH_LINE}>
                    {tileSettingsValue[tileSettings.sixthLine]}
                </p>
            )}
            {tileSettings.seventhLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.SEVENTH_LINE}>
                    {tileSettingsValue[tileSettings.seventhLine]}
                </p>
            )}
            {tileSettings.eighthLine && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.EIGHTH_LINE}>
                    {tileSettingsValue[tileSettings.eighthLine]}
                </p>
            )}
        </div>
        <div className="totalsContainer">
            {hasAllocationDimensions && (tileSettings.showUnits || tileSettings.showDoors) && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.UNITS_DOORS}>
                    {tileSettings.showUnits && formatMessage("Units")}
                    {tileSettings.showUnits && tileSettings.showDoors && " / "}
                    {tileSettings.showDoors && formatMessage("Doors")}
                </p>
            )}
            {tileSettings.showTotalCost && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.TOTAL_COST}>
                    {hasAllocationDimensions ? formatMessage("Total Cost") : formatMessage("Cost (ea)")}
                </p>
            )}
            {tileSettings.showTotalRetail && (
                <p data-test={DATATEST.ASSORTMENTS.TILE.SETTINGS.MODAL.PREVIEW.TOTAL_RETAIL}>
                    {hasAllocationDimensions ? formatMessage("Total Retail") : formatMessage("Retail (ea)")}
                </p>
            )}
        </div>
    </div>
);
